import React, { useState } from "react";
import { FaChevronDown, FaChevronRight, FaFilePdf, FaFileExcel } from "react-icons/fa";
import "./BalanceSheet.css";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BalanceSheet = ({ data }) => {
  const [assetsOpen, setAssetsOpen] = useState(false);
  const [liabilitiesOpen, setLiabilitiesOpen] = useState(false);
  const [equityOpen, setEquityOpen] = useState(false);

  const [openAssetCategory, setOpenAssetCategory] = useState(null);
  const [openLiabilityCategory, setOpenLiabilityCategory] = useState(null);
  const [openEquityCategory, setOpenEquityCategory] = useState(null);

  if (!data) return <p>Loading...</p>;

  const groupBy = (type) => {
    const grouped = {};
    data.items
      .filter((i) => i.type === type)
      .forEach((i) => {
        if (!grouped[i.category]) grouped[i.category] = [];
        grouped[i.category].push(i);
      });
    return grouped;
  };

  const assets = groupBy("asset");
  const liabilities = groupBy("liability");
  const equity = groupBy("equity");

  const getTotal = (items) => items.reduce((sum, i) => sum + i.amount, 0);

  // Export to Excel
  const exportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data.items);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Balance Sheet");
    XLSX.writeFile(workbook, "BalanceSheet.xlsx");
  };

  // Export to PDF
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Balance Sheet", 14, 20);

    const tableData = data.items.map((i) => [
      i.type,
      i.category,
      i.name,
      i.amount.toLocaleString(),
    ]);

    autoTable(doc, {
      head: [["Type", "Category", "Account", "Amount"]],
      body: tableData,
      startY: 25,
      theme: "striped",
      headStyles: { fillColor: [13, 110, 253] }, // Rekonic blue header
    });

    doc.save("BalanceSheet.pdf");
  };

  return (
    <div className="balance-sheet-container p-4">
      <h2 className="text-primary mb-4">Balance Sheet</h2>

      {/* Export Buttons */}
      <div className="d-flex flex-wrap align-items-center mb-3 gap-2">
        <button className="btn btn-sm btn-primary ms-auto" onClick={exportPDF}>
          <FaFilePdf className="me-1" /> Export PDF
        </button>
        <button className="btn btn-sm btn-success" onClick={exportExcel}>
          <FaFileExcel className="me-1" /> Export Excel
        </button>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-4 mb-2">
          <div className="card summary-card asset-card shadow-sm">
            <div className="card-body text-center">
              <h6 className="text-muted">Total Assets</h6>
              <h4 className="text-primary">${data.total_assets.toLocaleString()}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-2">
          <div className="card summary-card liability-card shadow-sm">
            <div className="card-body text-center">
              <h6 className="text-muted">Total Liabilities</h6>
              <h4 className="text-warning">${data.total_liabilities.toLocaleString()}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-2">
          <div className="card summary-card equity-card shadow-sm">
            <div className="card-body text-center">
              <h6 className="text-muted">Total Equity</h6>
              <h4 className="text-success">${data.total_equity.toLocaleString()}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Accordion Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          <table className="table table-hover mb-0 balance-sheet-table">
            <thead className="thead-light">
              <tr>
                <th>Description</th>
                <th className="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              {/* ASSETS */}
              <tr
                className="accordion-header asset-header"
                onClick={() => setAssetsOpen(!assetsOpen)}
              >
                <td>{assetsOpen ? <FaChevronDown /> : <FaChevronRight />} Assets</td>
                <td className="text-end">${data.total_assets.toLocaleString()}</td>
              </tr>
              {assetsOpen &&
                Object.keys(assets).map((cat) => (
                  <React.Fragment key={cat}>
                    <tr
                      className="accordion-subheader"
                      onClick={() =>
                        setOpenAssetCategory(openAssetCategory === cat ? null : cat)
                      }
                    >
                      <td style={{ paddingLeft: "20px" }}>
                        {openAssetCategory === cat ? <FaChevronDown /> : <FaChevronRight />} {cat}
                      </td>
                      <td className="text-end">${getTotal(assets[cat]).toLocaleString()}</td>
                    </tr>
                    {openAssetCategory === cat &&
                      assets[cat].map((item, i) => (
                        <tr key={i} className="accordion-item">
                          <td style={{ paddingLeft: "40px" }}>{item.name}</td>
                          <td className="text-end">${item.amount.toLocaleString()}</td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
              {/* LIABILITIES */}
              <tr
                className="accordion-header liability-header"
                onClick={() => setLiabilitiesOpen(!liabilitiesOpen)}
              >
                <td>{liabilitiesOpen ? <FaChevronDown /> : <FaChevronRight />} Liabilities</td>
                <td className="text-end">${data.total_liabilities.toLocaleString()}</td>
              </tr>
              {liabilitiesOpen &&
                Object.keys(liabilities).map((cat) => (
                  <React.Fragment key={cat}>
                    <tr
                      className="accordion-subheader"
                      onClick={() =>
                        setOpenLiabilityCategory(openLiabilityCategory === cat ? null : cat)
                      }
                    >
                      <td style={{ paddingLeft: "20px" }}>
                        {openLiabilityCategory === cat ? <FaChevronDown /> : <FaChevronRight />} {cat}
                      </td>
                      <td className="text-end">${getTotal(liabilities[cat]).toLocaleString()}</td>
                    </tr>
                    {openLiabilityCategory === cat &&
                      liabilities[cat].map((item, i) => (
                        <tr key={i} className="accordion-item">
                          <td style={{ paddingLeft: "40px" }}>{item.name}</td>
                          <td className="text-end">${item.amount.toLocaleString()}</td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
              {/* EQUITY */}
              <tr
                className="accordion-header equity-header"
                onClick={() => setEquityOpen(!equityOpen)}
              >
                <td>{equityOpen ? <FaChevronDown /> : <FaChevronRight />} Equity</td>
                <td className="text-end">${data.total_equity.toLocaleString()}</td>
              </tr>
              {equityOpen &&
                Object.keys(equity).map((cat) => (
                  <React.Fragment key={cat}>
                    <tr
                      className="accordion-subheader"
                      onClick={() =>
                        setOpenEquityCategory(openEquityCategory === cat ? null : cat)
                      }
                    >
                      <td style={{ paddingLeft: "20px" }}>
                        {openEquityCategory === cat ? <FaChevronDown /> : <FaChevronRight />} {cat}
                      </td>
                      <td className="text-end">${getTotal(equity[cat]).toLocaleString()}</td>
                    </tr>
                    {openEquityCategory === cat &&
                      equity[cat].map((item, i) => (
                        <tr key={i} className="accordion-item">
                          <td style={{ paddingLeft: "40px" }}>{item.name}</td>
                          <td className="text-end">${item.amount.toLocaleString()}</td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BalanceSheet;
